// frontend/src/modules/projects/components/EditBudgetDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { useApi } from '../../../design-system/useApi';
import { projectsApi } from '../api';
import type { Project } from '../types';

interface Client { id: string; name: string; }

const NONE = '__none__';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  project: Project;
  onSaved: () => void;
}

export function EditBudgetDialog({ open, onOpenChange, project, onSaved }: Props) {
  const clientsQ = useApi<{ clients: Client[] }>(open ? '/api/invoicing/clients' : null, [open]);
  const [clientId, setClientId] = React.useState<string>(NONE);
  const [budgetAmount, setBudgetAmount] = React.useState('');
  const [budgetHours, setBudgetHours] = React.useState('');
  const [hourlyRate, setHourlyRate] = React.useState('');
  const [billableDefault, setBillableDefault] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setClientId(project.client_id ?? NONE);
    setBudgetAmount(project.budget_amount != null ? String(project.budget_amount) : '');
    setBudgetHours(project.budget_hours != null ? String(project.budget_hours) : '');
    setHourlyRate(project.hourly_rate != null ? String(project.hourly_rate) : '');
    setBillableDefault(project.billable_default ?? true);
  }, [open, project]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await projectsApi.updateProject(project.id, {
        client_id: clientId === NONE ? null : clientId,
        budget_amount: budgetAmount.trim() ? Number(budgetAmount) : null,
        budget_hours: budgetHours.trim() ? Number(budgetHours) : null,
        hourly_rate: hourlyRate.trim() ? Number(hourlyRate) : null,
        billable_default: billableDefault,
      });
      toast.success('Budget saved');
      onSaved();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit budget &amp; billing</DialogTitle>
          <DialogDescription>Set a client, budget, and default rate so you can invoice tracked time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Client (for invoicing)</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger><SelectValue placeholder="(none)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>(none)</SelectItem>
                {(clientsQ.data?.clients ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Budget amount</Label>
              <Input type="number" min="0" step="0.01" value={budgetAmount} onChange={(e) => setBudgetAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div className="grid gap-1.5">
              <Label>Budget hours</Label>
              <Input type="number" min="0" step="0.25" value={budgetHours} onChange={(e) => setBudgetHours(e.target.value)} placeholder="0" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Default hourly rate</Label>
            <Input type="number" min="0" step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="0.00" />
          </div>
          <label className="flex items-center gap-2.5 text-[13px] text-foreground cursor-pointer select-none">
            <input type="checkbox" checked={billableDefault} onChange={(e) => setBillableDefault(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-saffron" />
            New time entries are billable by default
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
